import { FloatingArrow } from "@floating-ui/react";
import React, { ComponentPropsWithoutRef, LegacyRef, Ref } from "react";
import { clsx } from "clsx";
import MiscellaneousXSmall from "../icons/MiscellaneousXSmall.tsx";
import { useIntl } from "react-intl";
import styles from "./FloatingBoxWithArrow.module.scss";
import deepmerge from "deepmerge";

type AllArrowProps = ComponentPropsWithoutRef<typeof FloatingArrow>;

interface FloatingBoxProps extends Pick<AllArrowProps, "context"> {
  children: React.ReactNode;
  setFloating: LegacyRef<HTMLDivElement>;
  floatingStyles: React.CSSProperties;
  arrowRef: Ref<SVGSVGElement>;
  onClose?: () => void;
  arrowStaticOffset: AllArrowProps["staticOffset"];
  className?: string;
  role?: string;
  color?: "white" | "black";
  maxWidth?: string | number;
}

const FloatingBoxWithArrow = ({
  context,
  children,
  setFloating,
  floatingStyles,
  arrowRef,
  onClose,
  arrowStaticOffset,
  className,
  role,
  color = "white",
  maxWidth,
}: Partial<FloatingBoxProps>) => {
  const intl = useIntl();

  // This helps support a case where you want to instantiate the component bu
  // bring in the required floating props by way of a clone
  if (!setFloating || !arrowRef || !context || !floatingStyles) {
    return null;
  }

  return (
    <div
      ref={setFloating}
      style={deepmerge(floatingStyles, { maxWidth })}
      role={role}
      className={clsx(
        "vst-tooltip",
        "vst-tooltip-option-arrow",
        color === "white" && "vst-tooltip-variant-white",
        styles.container,
        className
      )}
    >
      <FloatingArrow
        ref={arrowRef}
        context={context}
        fill={color === "white" ? "white" : "var(--vst-cloud-10)"}
        width={16}
        height={8}
        tipRadius={1}
        staticOffset={arrowStaticOffset}
      />
      {onClose && (
        <button
          aria-label={intl.formatMessage({ defaultMessage: "Close" })}
          data-tooltip-content={intl.formatMessage({ defaultMessage: "Close" })}
          onClick={onClose}
          className={clsx(
            "vst-icon-button-circle",
            "vst-icon-button-size-small",
            color === "white"
              ? "vst-icon-button-variant-black"
              : "vst-icon-button-variant-white",
            styles.closeButton
          )}
        >
          <MiscellaneousXSmall />
        </button>
      )}
      {children}
    </div>
  );
};

export default FloatingBoxWithArrow;
